import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DashboardLayout } from '@/components/layout'
import { atsNav } from '@/lib/atsNav'
import { currentTenant, currentUser, auditLog, type AuditEntry } from '@/lib/mocks'
import { Badge } from '@/components/ui/badge'
import { DataList } from '@/components/ui/data-list'
import { Tabs } from '@/components/ui/tabs'
import { PageHeader } from '@/lib/atsUi'

export const Route = createFileRoute('/audit')({ component: AuditPage })

type FilterValue = 'all' | 'user' | 'agent' | 'system'

function actorBadgeVariant(actorType: AuditEntry['actorType']): 'info' | 'success' | 'warning' {
  if (actorType === 'user') return 'info'
  if (actorType === 'agent') return 'success'
  return 'warning'
}

function actorTypeLabel(actorType: AuditEntry['actorType']): string {
  if (actorType === 'user') return 'Person'
  if (actorType === 'agent') return 'Agent'
  return 'System'
}

function AuditPage() {
  const [filter, setFilter] = useState<FilterValue>('all')

  const sorted = [...auditLog].reverse()
  const filtered = filter === 'all'
    ? sorted
    : filter === 'agent'
      ? sorted.filter((e) => e.actorType === 'agent')
      : filter === 'user'
        ? sorted.filter((e) => e.actorType === 'user')
        : sorted.filter((e) => e.actorType === 'system')

  return (
    <DashboardLayout title="Audit log" navSections={atsNav} tenant={currentTenant} user={currentUser}>
      <PageHeader
        title="Audit log"
        subtitle="Every action — by you, your team, and the agents."
      />

      <Tabs
        items={[
          { value: 'all', label: 'All' },
          { value: 'agent', label: 'Agent' },
          { value: 'user', label: 'Human' },
          { value: 'system', label: 'System' },
        ]}
        value={filter}
        onChange={(v) => setFilter(v as FilterValue)}
      />

      <DataList
        items={filtered}
        getKey={(e) => e.id}
        pageSize={10}
        empty="No activity."
        renderRow={(entry) => (
          <div className="flex items-start justify-between gap-4 px-4 py-3.5 hover:bg-muted/40 transition-colors">
            <div className="flex items-start gap-3 min-w-0">
              <Badge variant={actorBadgeVariant(entry.actorType)} className="mt-0.5 shrink-0 text-[10px] uppercase tracking-wide">
                {actorTypeLabel(entry.actorType)}
              </Badge>
              <div className="min-w-0">
                <span className="font-medium text-sm text-foreground">{entry.actor}</span>
                <span className="text-sm text-muted-foreground"> — {entry.action}</span>
              </div>
            </div>
            <div className="flex items-start gap-4 shrink-0 text-right">
              <span className="text-sm text-muted-foreground hidden sm:inline">{entry.entity}</span>
              <span className="text-xs text-muted-foreground whitespace-nowrap pt-0.5" title={entry.at}>{entry.time}</span>
            </div>
          </div>
        )}
      />
    </DashboardLayout>
  )
}
